declare function ftLayerHas(row: unknown, key: string): boolean;
import { userChoiceOptions } from '../janus-engine/display/user-choice-selector';
import type { ItemFieldRow } from '../janus-engine/compile/item-field-reader';
// Fill deferred choice rows from raw authorized catalog inputs. The browser
// evaluates each authored spec and the existing select renderer escapes it.
// Unsupported specs and failed reads remain named in the affected row.

import { decodeHtmlEntitiesForPlainText } from '../shared/decode-html-entities';
import { renderSpellChoiceSelectRead } from './primitives';

const ENDPOINT = 'options/api/creature_sheet_catalog_inc.php';

type UcOption = { item_id?: string; item_name?: string; level?: string };
type HydrateWindow = Window & {
    initializeTomSelect?: () => void;
};

// The authorized candidate catalog is shared by every choice source on this page.
let choiceCatalog: Promise<ItemFieldRow[]> | undefined;
export function fetchChoiceCatalog(): Promise<ItemFieldRow[]> {
    if (choiceCatalog) return choiceCatalog;
    const base = typeof BASE_URL === 'string' ? BASE_URL : '/';
    choiceCatalog = fetch(base + ENDPOINT, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: 'user-choice' }),
    }).then(r => r.ok ? r.json() : null).then((body: unknown) => {
        const record = body && typeof body === 'object' ? body as Record<string, unknown> : null;
        if (!record?.catalog || !Array.isArray(record.ids)) throw new Error('unported:user-choice:catalog-not-held');
        const catalog = record.catalog as Record<string, ItemFieldRow>;
        return record.ids.map(id => catalog[String(id)]).filter((row): row is ItemFieldRow => !!row);
    });
    return choiceCatalog;
}

function choiceOptions(layer: string, spec: string, catalog: ItemFieldRow[]): UcOption[] {
    if (!spec) throw new Error('unported:user-choice:spec-not-held');
    return userChoiceOptions(layer, spec, catalog, {
        value: (row, key) => ftLayerValue(row, key), entries: (row, key) => ftLayerEntries(row, key),
        has: (row, key) => ftLayerHas(row, key), field: (row, key) => ftItemsCol(row, key),
        uuid: entry => typeof entry === 'string' ? entry : String(ftItemsCol(entry, 'item_id') ?? ''),
    }).map(option => ({ ...option, item_name: decodeHtmlEntitiesForPlainText(option.item_name) }));
}

function choiceRow(options: UcOption[], catalog: ItemFieldRow[], layer: string, source: string, saved: string): HTMLElement {
    const sameId = (id: unknown): boolean => String(id ?? '').trim().toUpperCase() === saved.toUpperCase();
    // Keep a saved identity if its definition left the offered pool, just as the
    // native creator does. This display entry does not change eligibility.
    if (saved && !options.some(option => sameId(option.item_id))) {
        const held = catalog.find(row => sameId(ftItemsCol(row, 'item_id')));
        options.push({ item_id: saved, item_name: held ? decodeHtmlEntitiesForPlainText(String(ftItemsCol(held, 'item_name') ?? saved)) : `Saved choice (unavailable): ${saved}`,
            level: held ? String(ftLayerValue(held, 'spell_level') ?? ftLayerValue(held, 'menta_level') ?? '') : '' });
    }
    const template = document.createElement('template');
    template.innerHTML = renderSpellChoiceSelectRead(layer, source, options);
    const rendered = template.content.querySelector('select');
    if (!rendered) throw new Error('unported:user-choice:select-not-held');
    const selected = [...rendered.options].find(option => saved && sameId(option.value));
    if (selected) selected.selected = true;
    return template.content.firstElementChild as HTMLElement;
}

function fillMountedSelect(select: HTMLSelectElement, rendered: HTMLElement): void {
    // Preserve the caller's id/name, dedup family and delegated change handler.
    const options = rendered.querySelector('select')!;
    const selected = options.value;
    select.replaceChildren(...options.childNodes);
    select.value = selected;
    select.classList.add('ts-select');
    select.classList.remove('select_user_choice');
    select.disabled = false;
    select.removeAttribute('data-ft-uc-pending');
}

/**
 * Fill every un-hydrated user-choice loader row inside `container`. Safe to call repeatedly
 * (rows are claimed synchronously via data-ft-uc-done before any await), and a no-op — not even a
 * DOM query cost worth caring about — on the overwhelmingly common card that has no such row.
 *
 * A pool that comes back empty means the spec resolved to nothing pickable, which is exactly the
 * old `return ''` outcome: drop the row so no empty control is left behind.
 */
export function ftHydrateUserChoiceOptions(container: HTMLElement): void {
    if (!container) return;
    const rows = container.querySelectorAll<HTMLElement>('[data-ft-uc-pending]:not([data-ft-uc-done])');
    if (!rows.length) return;

    const claimed: HTMLElement[] = [];
    rows.forEach(row => {
        row.setAttribute('data-ft-uc-done', '1');
        if (!row.getAttribute('data-ft-uc-source') || !row.getAttribute('data-ft-uc-layer')) row.remove();
        else claimed.push(row);
    });
    if (!claimed.length) return;
    const refuse = (row: HTMLElement, error: unknown): void => {
        if (!row.isConnected) return;
        row.className += ' text-danger';
        row.setAttribute('data-ft-display-refusal', '1');
        const message = error instanceof Error ? error.message : 'unported:user-choice:catalog-read-failed';
        if (row.tagName === 'SELECT') {
            const option = document.createElement('option');
            option.value = ''; option.textContent = message;
            row.replaceChildren(option);
        } else row.textContent = message;
    };
    void fetchChoiceCatalog().then(catalog => {
        let painted = false;
        for (const row of claimed) {
            if (!row.isConnected) continue;
            try {
                const layerKey = row.getAttribute('data-ft-uc-layer') || '';
                const sourceId = row.getAttribute('data-ft-uc-source') || '';
                const options = choiceOptions(layerKey, row.getAttribute('data-ft-uc-spec') || '', catalog);
                const saved = (row.getAttribute('data-user-choice-selected') ?? '').trim();
                if (row.tagName !== 'SELECT' && options.length === 0 && !saved) { row.remove(); continue; }
                const rendered = choiceRow(options, catalog, layerKey, sourceId, saved);
                if (row.tagName === 'SELECT') fillMountedSelect(row as HTMLSelectElement, rendered);
                else row.replaceWith(rendered);
                painted = true;
            } catch (error) { refuse(row, error); }
        }
        const host = window as HydrateWindow;
        if (painted && typeof host.initializeTomSelect === 'function') {
            try { host.initializeTomSelect(); } catch { /* select stays a plain <select> */ }
        }
    }).catch(error => { claimed.forEach(row => refuse(row, error)); });
}

Object.assign(window, { ftHydrateUserChoiceOptions });

// A connected mount works for direct PHP and innerHTML/AJAX insertion without
// a body-wide observer or importing this stateful module into another bundle.
if (typeof customElements !== 'undefined' && !customElements.get('ft-user-choice')) {
    customElements.define('ft-user-choice', class extends HTMLElement {
        connectedCallback(): void {
            // Parser-created elements can connect before their children exist.
            if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => ftHydrateUserChoiceOptions(this), { once: true });
            else ftHydrateUserChoiceOptions(this);
        }
    });
}
